/**
 * What a row can do, in one shape for the table row and the card footer.
 *
 * Every table had its own: a kebab, three text buttons wrapping in a cell, an
 * inline link, icon-only buttons, a greyed Save beside a greyed Remove. One
 * rule instead: at most one inline action, the rest in a kebab, and an action
 * that cannot run is either absent or in the menu with its reason.
 *
 * @module components/ui/RowActions
 */

import React from "react";

import { Button } from "./Button";
import { type ConfirmOptions, useOptionalConfirm } from "./ConfirmProvider";
import { Dropdown, type MenuItem } from "./Dropdown";
import { Icon } from "./Icon";

interface RowActionBase {
  key: string;
  label: string;
  icon?: string;
  onSelect: () => void;
  /**
   * Shows inline beside the kebab. Only the first one does; a refused action
   * never does, since an inline control that cannot run is the defect.
   */
  primary?: boolean;
  /** Shows the wait on an inline action while its write is in flight. */
  loading?: boolean;
  /** Colours a destructive action. It asks first unless `confirm` is `false`. */
  tone?: "default" | "danger";
  /** The question put to the reader. Defaults to one built from the label. */
  confirm?: Pick<ConfirmOptions, "title" | "body" | "confirmLabel" | "data-testid"> | false;
  "data-testid"?: string;
}

/** A refusal always carries its reason, so nothing is greyed in silence. */
export type RowAction = RowActionBase &
  ({ disabled?: false; disabledReason?: never } | { disabled: true; disabledReason: string });

/** What a collection offers on each row. */
export interface RowActionsSpec<Row> {
  /** Names the row for the kebab: `Actions for <label>`. */
  label: (row: Row) => string;
  /** Falsy entries are dropped, so a screen can write `cond && {…}`. */
  items: (row: Row) => readonly (RowAction | false | null | undefined)[];
  /** Names the kebab, for a spec that already opens it. */
  testId?: (row: Row) => string;
}

function present(items: ReturnType<RowActionsSpec<unknown>["items"]>): readonly RowAction[] {
  return items.filter((item): item is RowAction => Boolean(item));
}

export interface RowActionControlsProps<Row> {
  row: Row;
  spec: RowActionsSpec<Row>;
  /** Raises targets to 44px, for a card on a touch screen. */
  touch?: boolean;
}

/** Whether choosing the action asks first: a danger action does unless it opts out. */
function asks(action: RowAction): boolean {
  return action.confirm !== false && (action.tone === "danger" || Boolean(action.confirm));
}

/**
 * The inline action and the kebab for one row.
 *
 * @example
 * ```tsx
 * <RowActionControls row={programme} spec={{ label: (p) => p.title, items }} />
 * ```
 */
export function RowActionControls<Row>({
  row,
  spec,
  touch = false,
}: RowActionControlsProps<Row>): React.ReactElement | null {
  const confirm = useOptionalConfirm();
  const actions = present(spec.items(row));
  if (!confirm && actions.some(asks)) {
    throw new Error("A confirming row action needs a ConfirmProvider above it in the tree.");
  }
  const inline = actions.find((action) => action.primary && !action.disabled);
  const menu = actions.filter((action) => action !== inline);

  const run = (action: RowAction): void => {
    if (!asks(action) || !confirm) {
      action.onSelect();
      return;
    }
    const question = action.confirm || {
      title: `${action.label}?`,
      confirmLabel: action.label,
    };
    confirm({ ...question, tone: action.tone })
      .then((confirmed) => {
        if (confirmed) {
          action.onSelect();
        }
      })
      .catch(() => undefined);
  };

  if (!inline && menu.length === 0) {
    return null;
  }
  const target = touch ? "min-h-11 min-w-11" : "";
  const items: readonly MenuItem[] = menu.map((action) => ({
    key: action.key,
    label: action.label,
    icon: action.icon,
    tone: action.tone,
    disabled: action.disabled,
    disabledReason: action.disabledReason,
    onSelect: () => {
      run(action);
    },
    "data-testid": action["data-testid"],
  }));

  return (
    <div className="flex items-center justify-end gap-1">
      {inline && (
        <Button
          size="sm"
          variant="outline"
          icon={inline.icon}
          tone={inline.tone}
          loading={inline.loading}
          className={target}
          onClick={() => {
            run(inline);
          }}
          data-testid={inline["data-testid"]}
        >
          {inline.label}
        </Button>
      )}
      {menu.length > 0 && (
        <Dropdown
          aria-label={`Actions for ${spec.label(row)}`}
          trigger={<Icon name="dots-three" weight="bold" className="text-xl" aria-hidden />}
          triggerAppearance="ghost"
          triggerClassName={target}
          items={items}
          data-testid={spec.testId?.(row)}
        />
      )}
    </div>
  );
}
